import { useCallback, useMemo } from 'react';

import { useSJDatabase } from '@/shared/lib/sj-orm/hooks/use-sj-database';
import { BaseDto } from '@/shared/lib/sj-orm/models/base.dto';
import { PersonalIdentifierDocumentDto } from '@/shared/lib/sj-orm/models/document/personal-identifier.document.dto';
import { PrivateDocumentDto } from '@/shared/lib/sj-orm/models/document/private.document.dto';
import { useKeyPairStore } from '@/shared/store/decrypted-keypair.store';
import { AIStatus } from '@/shared/types';

import { isPersonalIdentifierDocumentDto, isPrivateDocumentDto } from '../../helpers';

export type CommonDocument = PrivateDocumentDto | PersonalIdentifierDocumentDto;

export const useDocuments = () => {
  const { keyPair } = useKeyPairStore();
  const sjDb = useSJDatabase();

  const sizes = useMemo(() => {
    const pids = sjDb.collections.personalIdentifiersDocuments;
    const privateDocs = sjDb.collections.privateDocuments;

    return {
      beneficiaryPersonal:
        pids?.findMany((item) => !!(item.beneficiaryId && item.beneficiaryId !== 'me')).length ?? 0,

      myPersonal: pids?.findMany((item) => item.beneficiaryId === 'me').length ?? 0,
      otherIds: pids?.findMany((item) => item.contactId !== undefined).length ?? 0,
      private: privateDocs?.size() ?? 0,
    };
  }, [keyPair, sjDb]);

  const getCfPersonalIds = useCallback(() => {
    return (
      sjDb.collections.personalIdentifiersDocuments?.findMany(
        (item) => item.beneficiaryId === 'me',
      ) ?? []
    );
  }, [keyPair, sjDb]);

  const getPersonalIds = useCallback(() => {
    const list = sjDb.collections.personalIdentifiersDocuments?.findMany(() => true);
    return list ?? [];
  }, [keyPair, sjDb]);

  const getPrivateDocuments = useCallback(() => {
    return sjDb.collections.privateDocuments?.findMany(() => true) ?? [];
  }, [keyPair, sjDb]);

  const getBeneficiaryPids = useCallback(() => {
    return (
      sjDb.collections.personalIdentifiersDocuments?.findMany(
        (item) => !!(item && item.beneficiaryId && item.beneficiaryId !== 'me'),
      ) ?? []
    );
  }, [keyPair, sjDb]);

  const getDocument = useCallback(
    (id: string) => {
      const pid = sjDb.collections.personalIdentifiersDocuments?.findById(id);
      if (pid) return pid as PersonalIdentifierDocumentDto;
      const pd = sjDb.collections.privateDocuments?.findById(id);
      if (pd) return pd as PrivateDocumentDto;
      return undefined;
    },
    [keyPair, sjDb],
  );

  // const attachFileToDocument = useCallback(
  //   (docId: DtoId, fileId: string) => {
  //     const file = sjDb.collections.fileMetaInfos?.findById(fileId);

  //     if (!file) {
  //       log.warn(`[attachFileToDocument] file not found ${fileId}`);
  //       return;
  //     }

  //     const filePath = file?.filePath;
  //     const pid = sjDb.collections.personalIdentifiersDocuments?.findById(docId);
  //     if (pid) {
  //       sjDb.collections.personalIdentifiersDocuments?.update({
  //         ...pid,
  //         attachments: [...(pid?.attachments ?? []), filePath],
  //       });
  //       return;
  //     }
  //     const pd = sjDb.collections.privateDocuments?.findById(docId);
  //     if (pd) {
  //       sjDb.collections.privateDocuments?.update({
  //         ...pd,
  //         scan: filePath,
  //       });
  //     }
  //   },
  //   [keyPair, sjDb],
  // );

  const addDocumentAttachments = useCallback(
    (documentData: CommonDocument, filesPaths: string[]) => {
      const isPid = isPersonalIdentifierDocumentDto(documentData);

      if (isPid) {
        const { attachments, ...data } = documentData;

        sjDb.collections.personalIdentifiersDocuments?.update({
          ...data,
          attachments: [...(attachments ?? []), ...filesPaths],
        });
      } else {
        const { attachments, ...data } = documentData;

        sjDb.collections.privateDocuments?.update({
          ...data,
          attachments: [...(attachments ?? []), ...filesPaths],
        });
      }
    },
    [sjDb],
  );

  const removeDocumentAttachments = useCallback(
    (documentData: CommonDocument, filesPaths: string[]) => {
      const isPid = isPersonalIdentifierDocumentDto(documentData);

      if (isPid) {
        sjDb.collections.personalIdentifiersDocuments?.update({
          ...documentData,
          attachments: documentData.attachments?.filter((path) => !filesPaths.includes(path)) ?? [],
        });
      } else {
        sjDb.collections.privateDocuments?.update({
          ...documentData,
          attachments: documentData.attachments?.filter((path) => !filesPaths.includes(path)) ?? [],
        });
      }
    },
    [sjDb],
  );

  const deleteDocument = useCallback(
    (id: string) => {
      const privateDoc = sjDb.collections.privateDocuments?.findById(id);
      if (privateDoc) {
        sjDb.collections.privateDocuments?.remove(privateDoc);
        return true;
      }

      const pid = sjDb.collections.personalIdentifiersDocuments?.findById(id);
      if (pid) {
        sjDb.collections.personalIdentifiersDocuments?.remove(pid);
        return true;
      }

      return false;
    },
    [sjDb, keyPair],
  );

  const verifyAiDocument = useCallback(
    (document: BaseDto) => {
      if (isPersonalIdentifierDocumentDto(document)) {
        sjDb.collections.personalIdentifiersDocuments?.update({
          ...document,
          aiStatus: AIStatus.VERIFIED,
        });
      }

      if (isPrivateDocumentDto(document)) {
        sjDb.collections.privateDocuments?.update({
          ...document,
          aiStatus: AIStatus.VERIFIED,
        });
      }
    },
    [sjDb],
  );

  return useMemo(
    () => ({
      sizes,
      getPersonalIds,
      getPrivateDocuments,
      getBeneficiaryPids,
      getCfPersonalIds,
      getDocument,
      removeDocumentAttachments,
      deleteDocument,
      verifyAiDocument,
      addDocumentAttachments,
      // attachFileToDocument,
    }),
    [
      sizes,
      getPersonalIds,
      getPrivateDocuments,
      getBeneficiaryPids,
      getCfPersonalIds,
      getDocument,
      removeDocumentAttachments,
      verifyAiDocument,
      addDocumentAttachments,
      // attachFileToDocument,
    ],
  );
};
